import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { authAxios } from "../../utils/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Shield, ShieldCheck, AlertOctagon, AlertTriangle, ShieldX, FileWarning } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  active:  { icon: ShieldCheck,   tone: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", label: "ACTIVE" },
  warning: { icon: AlertTriangle, tone: "text-amber-300 bg-amber-500/10 border-amber-500/30",     label: "WARNING" },
  frozen:  { icon: AlertOctagon,  tone: "text-red-300 bg-red-500/10 border-red-500/30",           label: "FROZEN" },
  blocked: { icon: ShieldX,       tone: "text-red-200 bg-red-600/20 border-red-600/40",           label: "BLOCKED" },
};

/**
 * UserGuardianCard — top card on /admin/users/:userId.
 *
 * Shows the user's current Guardian posture (account_status +
 * fraud_status), lets the admin override to ACTIVE/WARNING/FREEZE/BLOCK
 * with a reason, and renders the status_history timeline.
 */
export default function UserGuardianCard({ userId }) {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [casesCount, setCasesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [next, setNext] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, casesRes] = await Promise.all([
        authAxios.get(`/admin/fraud/user/${userId}/status`),
        authAxios.get(`/admin/fraud/cases?user_id=${encodeURIComponent(userId)}&limit=200`),
      ]);
      setStatus(statusRes.data.status);
      setHistory(statusRes.data.history || []);
      setCasesCount((casesRes.data.rows || []).length);
    } catch (e) {
      // Endpoint may 404 briefly during backend restart — silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (userId) load(); /* eslint-disable-next-line */ }, [userId]);

  const apply = async () => {
    if (!next) { toast.error("Pick a status"); return; }
    if (!window.confirm(`Set account_status="${next}" for this user?\n\nAll listed side-effects will fire.`)) return;
    setSaving(true);
    try {
      await authAxios.post(`/admin/fraud/user/${userId}/status`, {
        status: next,
        reason: reason || "admin manual override",
      });
      toast.success(`Status updated → ${next}`);
      setNext(""); setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const current = (status?.account_status || "active");
  const meta = STATUS_META[current] || STATUS_META.active;
  const Icon = meta.icon;

  return (
    <Card
      className={`border ${meta.tone} mb-6`}
      data-testid="user-guardian-card"
    >
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" />
          Guardian Status
          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider bg-black/30" data-testid="user-guardian-badge">
            {meta.label}
          </span>
          {status?.fraud_status && status.fraud_status !== "clean" && (
            <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 font-mono">
              fraud: {status.fraud_status}
            </span>
          )}
          {casesCount > 0 && (
            <Link
              to={`/admin/fraud/defender/${userId}`}
              className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 font-semibold"
              data-testid="user-guardian-view-cases"
            >
              <FileWarning className="w-3 h-3" />
              View {casesCount} Case{casesCount !== 1 ? "s" : ""}
            </Link>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-xs text-white/40">Loading…</div>
        ) : (
          <>
            {status?.reason && (
              <div className="text-xs text-white/70">
                <span className="text-white/40 uppercase text-[10px] tracking-wider mr-1">Reason</span>
                {status.reason}
              </div>
            )}
            {status?.updated_at && (
              <div className="text-[10px] text-white/40 font-mono">
                Last change {new Date(status.updated_at).toLocaleString()}
              </div>
            )}

            {/* Override controls */}
            <div className="pt-3 border-t border-white/5 space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-white/40">Status override</div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={next} onValueChange={setNext}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid="user-guardian-status-select">
                    <SelectValue placeholder="Set status…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">ACTIVE</SelectItem>
                    <SelectItem value="warning">WARNING</SelectItem>
                    <SelectItem value="frozen">FREEZE</SelectItem>
                    <SelectItem value="blocked">BLOCK</SelectItem>
                  </SelectContent>
                </Select>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (visible in timeline)"
                  className="h-8 px-2 text-xs bg-white/5 border border-white/10 rounded flex-1 min-w-[200px]"
                  data-testid="user-guardian-reason"
                />
                <Button
                  onClick={apply}
                  disabled={!next || saving}
                  className="h-8 text-xs"
                  data-testid="user-guardian-apply"
                >
                  {saving ? "Applying…" : "Apply"}
                </Button>
              </div>
            </div>

            {/* History */}
            {history.length > 0 && (
              <div className="pt-3 border-t border-white/5">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">
                  Status history — {history.length} change{history.length !== 1 ? "s" : ""}
                </div>
                <ul className="space-y-1 max-h-56 overflow-y-auto" data-testid="user-guardian-history">
                  {history.slice().reverse().map((h, i) => (
                    <li key={i} className="text-[11px] flex items-center gap-2 text-white/70">
                      <span className="font-mono text-white/40">{new Date(h.at).toLocaleString()}</span>
                      <span className="uppercase text-[9px] tracking-wider">
                        {h.from} <span className="text-white/30">→</span> <span className="text-white">{h.to}</span>
                      </span>
                      {h.reason && <span className="text-white/50 truncate">· {h.reason}</span>}
                      {h.admin_id && <span className="ml-auto text-[9px] text-white/30 font-mono">{h.admin_id.slice(0, 10)}…</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
